import { apiClient } from './api';
import type {
  NinerBreadthResponse,
  NinerBreadthFiltersResponse,
  NinerBreadthData,
  NinerBreadthFilters,
  FilterType,
  ZScoreSeriesResponse,
  ZScoreSeriesData,
} from '../types/ninerBreadth';

const DEFAULT_LOOKBACK_DAYS = 180;

export const ninerBreadthService = {
  /**
   * Fetch TD Z-Score aggregate data
   * Only one filter can be applied at a time (they don't stack)
   */
  async fetchAggregate(
    filterType: FilterType,
    filterValue: string,
    lookbackDays: number = DEFAULT_LOOKBACK_DAYS
  ): Promise<NinerBreadthData> {
    const params: Record<string, string> = {
      lookback_days: lookbackDays.toString(),
    };

    // Apply the selected filter
    switch (filterType) {
      case 'tickers':
        params.tickers = filterValue;
        break;
      case 'asset_type':
        params.asset_type = filterValue;
        break;
      case 'exchange':
        params.exchange = filterValue;
        break;
      case 'sector':
        params.sector = filterValue;
        break;
      case 'sub_sector':
        params.sub_sector = filterValue;
        break;
    }

    const result = await apiClient.get<NinerBreadthResponse>(
      '/api/market-data/td-zscore/aggregate/',
      params
    );

    if (result.status !== 'success') {
      throw new Error('API returned non-success status');
    }

    return result.data;
  },

  /**
   * Fetch available filter options for dropdowns
   */
  async fetchFilters(): Promise<NinerBreadthFilters> {
    const result = await apiClient.get<NinerBreadthFiltersResponse>(
      '/api/market-data/td-zscore/filters/'
    );

    if (result.status !== 'success') {
      throw new Error('API returned non-success status');
    }

    return result.data;
  },

  /**
   * Fetch z-score time series for oscillator subplot
   * @param filterType - Type of filter to apply
   * @param filterValue - Value for the filter
   * @param daysBack - Number of days of data to return (1-365, default 30)
   * @param lookbackDays - Days for z-score calculation (min 20, default 252)
   */
  async fetchZScoreSeries(
    filterType: FilterType,
    filterValue: string,
    daysBack: number = 30,
    lookbackDays: number = 252
  ): Promise<ZScoreSeriesData> {
    const params: Record<string, string> = {
      days_back: daysBack.toString(),
      lookback_days: lookbackDays.toString(),
    };

    // Apply the selected filter
    switch (filterType) {
      case 'tickers':
        params.tickers = filterValue;
        break;
      case 'asset_type':
        params.asset_type = filterValue;
        break;
      case 'exchange':
        params.exchange = filterValue;
        break;
      case 'sector':
        params.sector = filterValue;
        break;
      case 'sub_sector':
        params.sub_sector = filterValue;
        break;
    }

    const result = await apiClient.get<ZScoreSeriesResponse>(
      '/api/market-data/td-zscore/zscore-series/',
      params
    );

    if (result.status !== 'success') {
      throw new Error('API returned non-success status');
    }

    return result.data;
  },
};
